import { useMemo } from "react";
import {
  useMyFeed,
  useTasks,
  useLists,
  useCreateTask,
  useCompleteTask,
  useUpdateTask,
} from "../hooks/useTasks";
import { useUiStore } from "../state/uiStore";
import { taskToVM, feedItemToVM, groupTasks } from "../lib/taskVM";
import { useTaskExtras } from "../hooks/useTaskExtras";
import {
  applyView,
  applyGroup,
  groupPatch,
  groupCreateDefaults,
  type GroupField,
  type TaskGroup,
} from "../lib/viewEngine";
import { ViewTabs } from "../components/toolbar/ViewTabs";
import { ViewToolbar } from "../components/toolbar/ViewToolbar";
import type { TaskRowVM } from "../components/TaskRow";
import { ListLayout } from "../components/layouts/ListLayout";
import { BoardLayout } from "../components/layouts/BoardLayout";
import { GalleryLayout } from "../components/layouts/GalleryLayout";
import { TimelineLayout } from "../components/layouts/TimelineLayout";
import { QuickAdd } from "../components/QuickAdd";
import { dueDateToIso } from "../lib/date";
import { STATUS } from "../lib/design";

const STATUS_BOARD_ORDER = ["todo", "in_progress", "blocked", "done"];

export function MyTasksScreen() {
  const feed = useMyFeed();
  const tasks = useTasks();
  const lists = useLists();
  const createTask = useCreateTask();
  const completeTask = useCompleteTask();
  const updateTask = useUpdateTask();
  const { selectedTaskId, selectTask, dayFilter, listFilter, prefs } =
    useUiStore();
  const savedViews = useUiStore((s) => s.savedViews);
  const activeViewId = useUiStore((s) => s.activeViewId);
  const setActiveView = useUiStore((s) => s.setActiveView);
  const addView = useUiStore((s) => s.addView);
  const updateView = useUiStore((s) => s.updateView);
  const removeView = useUiStore((s) => s.removeView);
  const view = savedViews.find((v) => v.id === activeViewId) ?? savedViews[0]!;

  const extras = useTaskExtras(tasks.data);

  const vms = useMemo(() => {
    const byId = new Map((tasks.data ?? []).map((t) => [t.id, t]));
    const out: TaskRowVM[] = [];
    for (const f of feed.data ?? []) {
      if (f.status === "archived") continue; // asStatus coercerait en "todo"
      const native = f.source === "native" ? byId.get(f.id) : undefined;
      out.push(
        native ? taskToVM(native, lists.data, extras(native)) : feedItemToVM(f),
      );
    }
    return out.filter((t) => {
      if (dayFilter.length && (!t.dueKey || !dayFilter.includes(t.dueKey)))
        return false;
      if (listFilter.length) {
        const task = byId.get(t.id);
        if (!task?.list_id || !listFilter.includes(task.list_id)) return false;
      }
      return true;
    });
  }, [feed.data, tasks.data, lists.data, dayFilter, listFilter, extras]);

  /** Pipeline vue sauvegardée : filtre + tri, puis groupement du layout. */
  const viewVms = useMemo(() => applyView(vms, view), [vms, view]);
  // Board sans groupage explicite → colonnes par statut (comme le prototype).
  const effectiveGroupBy: GroupField =
    view.layout === "board" && view.groupBy === "none"
      ? "status"
      : view.groupBy;
  const isDefaultView =
    view.layout === "list" &&
    view.groupBy === "none" &&
    !view.filter.conditions.length;
  const groups = useMemo(() => groupTasks(viewVms), [viewVms]);

  const layoutGroups: TaskGroup[] = useMemo(() => {
    if (isDefaultView)
      return [
        {
          key: "overdue",
          label: "En retard",
          color: "var(--danger)",
          items: groups.overdue,
        },
        { key: "active", label: "Tâches actives", items: groups.active },
        { key: "done", label: "Terminées", items: groups.done },
      ].filter((g) => g.items.length > 0);
    let out = applyGroup(viewVms, effectiveGroupBy);
    // Board par statut : colonnes canoniques toujours visibles, même vides ;
    // cancelled/archived sortent du board (comme l'ancien BoardScreen).
    if (view.layout === "board" && effectiveGroupBy === "status") {
      out = out.filter((g) => STATUS_BOARD_ORDER.includes(g.key));
      for (const s of ["todo", "in_progress", "blocked", "done"] as const) {
        if (!out.some((g) => g.key === s))
          out.push({
            key: s,
            label: STATUS[s].label,
            color: STATUS[s].dot,
            items: [],
          });
      }
      out.sort(
        (a, b) =>
          STATUS_BOARD_ORDER.indexOf(a.key) - STATUS_BOARD_ORDER.indexOf(b.key),
      );
    }
    return out;
  }, [isDefaultView, groups, viewVms, effectiveGroupBy, view.layout]);

  const onAdd = (title: string) => {
    createTask.mutate({
      title,
      priority: prefs.defaultPriority,
      visibility: prefs.defaultVisibility,
      list_id: listFilter[0] ?? prefs.defaultListId ?? undefined,
      due_date:
        dayFilter[0] && dayFilter.length === 1
          ? dueDateToIso(dayFilter[0])
          : undefined,
    });
  };

  const onToggleDone = (t: TaskRowVM) => {
    if (t.status === "done") {
      updateTask.mutate({ id: t.id, body: { status: "todo" } });
    } else {
      completeTask.mutate(t.id);
    }
  };

  const canDrop = (key: string) => !!groupPatch(effectiveGroupBy, key);
  const onDropTask = (taskId: string, key: string) => {
    const patch = groupPatch(effectiveGroupBy, key);
    if (patch) updateTask.mutate({ id: taskId, body: patch });
  };

  /** Composer inline d'un groupe — création pré-remplie avec la valeur du
   * groupe ; statut appliqué en PATCH post-création (create sans status). */
  const composerFor = (key: string) => {
    if (isDefaultView) return null; // QuickAdd global suffit
    const d = groupCreateDefaults(effectiveGroupBy, key, lists.data ?? []);
    if (!d) return null;
    return (title: string) =>
      createTask.mutate(
        {
          title,
          priority: prefs.defaultPriority,
          visibility: prefs.defaultVisibility,
          list_id: listFilter[0] ?? prefs.defaultListId ?? undefined,
          ...d.create,
        },
        {
          onSuccess: (created) => {
            if (d.status && d.status !== "todo")
              updateTask.mutate({ id: created.id, body: { status: d.status } });
          },
        },
      );
  };

  const layoutProps = {
    groups: layoutGroups,
    selectedTaskId,
    onSelect: (id: string | null) => selectTask(id),
    onToggleDone,
    hiddenProps: view.hidden,
    canDrop,
    onDropTask,
    composerFor,
    composerBusy: createTask.isPending,
  };

  return (
    <>
      <div className="view-header">
        <div className="view-title">Mes tâches</div>
        <div className="view-caption">
          {groups.overdue.length + groups.active.length} ouvertes ·{" "}
          {groups.overdue.length} en retard · {groups.done.length} terminées
        </div>
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 12,
            marginTop: 10,
            flexWrap: "wrap",
          }}
        >
          <ViewTabs
            views={savedViews}
            activeId={view.id}
            onSelect={setActiveView}
            onAdd={addView}
            onRename={(id, name) => updateView(id, { name })}
            onRemove={removeView}
          />
          <span style={{ marginLeft: "auto" }}>
            <ViewToolbar
              view={view}
              onPatch={(patch) => updateView(view.id, patch)}
              listNames={(lists.data ?? []).map((l) => l.name)}
              layouts={["list", "board", "gallery", "timeline"]}
            />
          </span>
        </div>
      </div>
      <div className="main-scroll">
        <div style={{ marginTop: 14 }}>
          <QuickAdd onAdd={onAdd} busy={createTask.isPending} />
        </div>
        {feed.isLoading && <div className="empty-state">Chargement…</div>}
        {feed.isError && (
          <div className="empty-state">Impossible de charger les tâches.</div>
        )}
        {view.layout === "board" ? (
          <BoardLayout
            {...layoutProps}
            columnOrder={view.columnOrder}
            onReorderColumns={(order) =>
              updateView(view.id, { columnOrder: order })
            }
          />
        ) : view.layout === "gallery" ? (
          <GalleryLayout {...layoutProps} />
        ) : view.layout === "timeline" ? (
          <TimelineLayout {...layoutProps} />
        ) : (
          // calendar reste un écran dédié — fallback liste ici.
          <ListLayout {...layoutProps} />
        )}
        {!feed.isLoading && viewVms.length === 0 && (
          <div className="empty-state">
            Aucune tâche
            {dayFilter.length ||
            listFilter.length ||
            view.filter.conditions.length
              ? " pour ce filtre."
              : " — ajoutez-en une ci-dessus."}
          </div>
        )}
      </div>
    </>
  );
}
